import { create } from 'zustand'
import { EnumTradeInActivationStrategy } from '../generated/graphql'
import { useAddFlowStore } from './addFlow'

/**
 * Authenticated Core session. The real `token` (obtained by exchanging the login
 * accessToken via ConsumerLogin) is sent as the `authorization` header on every Core call —
 * the Apollo auth link reads it from here with `useAuthStore.getState().token`.
 *
 * `config` carries the per-shop flags Core returns at login; `config.tradeInComments` gates
 * the optional "Your story" step, so the flow's step list is driven by the real session.
 */
export type AuthStatus = 'idle' | 'authenticating' | 'authenticated' | 'error'

/**
 * When the customer's voucher is issued — a per-brand policy (Core's `TradeInConfig.voucherStrategy`)
 * that changes some "when do you get your reward" wording (see the localization strategy-variant
 * strings). Aliased to Core's generated enum so there's one source of truth.
 *   INSTANT  — issued up front; the brand trusts the customer to send the items afterwards.
 *   HANDIN   — issued once the parcel is scanned in at a drop-off point / post office.
 *   RECEIVED — (default) issued after our warehouse has received and checked the items.
 */
export const VoucherStrategy = EnumTradeInActivationStrategy
export type VoucherStrategy = EnumTradeInActivationStrategy

/**
 * The login accessToken is kept in `sessionStorage` so a page refresh (or in-tab reload of the host
 * storefront) keeps the consumer logged in, while closing the tab/browser forgets them — sessionStorage
 * is scoped to the tab session and cleared on close, which is exactly the desired "remember on refresh,
 * forget on close" behaviour. We persist only the login token (re-exchanged for a session on each load)
 * and the `selfCreated` flag the UI gates on, never the exchanged bearer token. Guarded so a browser
 * with storage disabled (private mode / blocked cookies) degrades to in-memory-only, not a crash.
 */
const LOGIN_TOKEN_KEY = 'ti.loginToken'
const SELF_CREATED_KEY = 'ti.consumerSelfCreated'

interface PersistedLogin {
  loginToken: string | null
  consumerSelfCreated: boolean
}

function readPersistedLogin(): PersistedLogin {
  try {
    return {
      loginToken: sessionStorage.getItem(LOGIN_TOKEN_KEY),
      consumerSelfCreated: sessionStorage.getItem(SELF_CREATED_KEY) === 'true',
    }
  } catch {
    return { loginToken: null, consumerSelfCreated: false }
  }
}

function writePersistedLogin(token: string, selfCreated: boolean): void {
  try {
    sessionStorage.setItem(LOGIN_TOKEN_KEY, token)
    sessionStorage.setItem(SELF_CREATED_KEY, String(selfCreated))
  } catch {
    /* storage unavailable — session stays in-memory only */
  }
}

function clearPersistedLogin(): void {
  try {
    sessionStorage.removeItem(LOGIN_TOKEN_KEY)
    sessionStorage.removeItem(SELF_CREATED_KEY)
  } catch {
    /* nothing to clear if storage is unavailable */
  }
}

export interface ShopConfig {
  brandName?: string
  locale?: string
  currency?: string
  shopCurrency?: string
  /** Maximum voucher value, denominated in shop currency. 0 = no maximum. */
  maximumVoucher?: number
  allowedCountry?: boolean
  lockEmail?: boolean
  requestPotentialsSupported?: boolean
  /** Brand enables the "Your story" step. */
  tradeInComments?: boolean
  /**
   * When the voucher is issued (INSTANT/HANDIN/RECEIVED), from Core's `TradeInConfig.voucherStrategy`.
   * Drives the strategy-variant wording via the localization layer. Seeded pre-login by the front page
   * and set at login by AuthProvider; absent → the localization layer defaults to RECEIVED.
   */
  voucherStrategy?: VoucherStrategy
  // --- from TradeInConfig, fetched after login (needed by the finder) ---
  /** Path segment for the (current) search endpoint. */
  brandHouseId?: string
  /** Fallback image for items with no picture. */
  defaultImage?: string
  /** Whether the "Search catalogue" method is available — gates the "Find your item" section. */
  productSearchEnabled?: boolean
  /** Whether the shop runs whole-sale categories — gates the "add by category" fallback tile. */
  wholeSaleActive?: boolean
  /** Per-brand input mask for numeric (SKU/EAN) entry. */
  productSearchMask?: string
}

interface Session {
  token: string
  consumerId: string
  shopId: string
  config: ShopConfig
  /**
   * Whether this session's consumer is self-created (wholesale / "bought elsewhere"). Passed in
   * explicitly so an embed-token login always sets the correct value rather than inheriting a stale
   * `sessionStorage` flag from an earlier wholesale login in the same browser session.
   */
  selfCreated: boolean
}

interface AuthState {
  status: AuthStatus
  token: string | null
  consumerId: string | null
  shopId: string | null
  config: ShopConfig
  error: string | null
  /**
   * An accessToken obtained client-side on the login page (SMS verify / wholesale create). Setting
   * it makes the App gate re-render and hand off to AuthProvider, which exchanges it for a session.
   * (Email login instead returns via the magic link's `?login=` param.)
   */
  loginToken: string | null
  /**
   * Whether this session's consumer was just created by us via the wholesale / "bought elsewhere"
   * login (a `selfCreated` consumer). Such a consumer has no past orders, so we skip the order-feed
   * import (ConsumerRequestPotentials) — mirrors v1's `!loginContext.wholesale` guard.
   */
  consumerSelfCreated: boolean
  setAuthenticating: () => void
  setSession: (session: Session) => void
  setError: (message: string) => void
  setLoginToken: (token: string, selfCreated?: boolean) => void
  /** Merge a partial config into the current one (e.g. the front page seeding voucherStrategy pre-login). */
  mergeConfig: (partial: Partial<ShopConfig>) => void
  logout: () => void
}

const persisted = readPersistedLogin()

export const useAuthStore = create<AuthState>((set) => ({
  status: 'idle',
  token: null,
  consumerId: null,
  shopId: null,
  config: {},
  error: null,
  // Hydrate the login token from sessionStorage so a refresh re-authenticates without a fresh login.
  loginToken: persisted.loginToken,
  consumerSelfCreated: persisted.consumerSelfCreated,
  setAuthenticating: () => set({ status: 'authenticating', error: null }),
  setSession: ({ token, consumerId, shopId, config, selfCreated }) => {
    useAddFlowStore.getState().startSession(shopId, consumerId)
    set({
      status: 'authenticated',
      token,
      consumerId,
      shopId,
      config,
      consumerSelfCreated: selfCreated,
      error: null,
    })
  },
  setError: (message) => set({ status: 'error', error: message }),
  setLoginToken: (token, selfCreated = false) => {
    writePersistedLogin(token, selfCreated)
    set({ loginToken: token, consumerSelfCreated: selfCreated })
  },
  mergeConfig: (partial) => set((s) => ({ config: { ...s.config, ...partial } })),
  logout: () => {
    clearPersistedLogin()
    useAddFlowStore.getState().endSession()
    set({
      status: 'idle',
      token: null,
      consumerId: null,
      config: {},
      error: null,
      loginToken: null,
      consumerSelfCreated: false,
    })
  },
}))
